import { useMemo } from 'react';
import { Flex } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	getEmploymentEmployers,
	getPersonEmploymentRecords,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import { EmploymentRecordItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/EmploymentRecordsCard/EmploymentRecordItem.tsx';
import { getEmployerNameById } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/EmploymentRecordsCard/employmentRecords.utils.ts';

interface Props {
	personId: string | undefined;
}

export const EmploymentRecordsCard = ({ personId }: Props) => {
	const tMipManage = useTranslationMipManage();

	const recordsQuery = useQuery({
		queryKey: [API_PATHS.PERSONS_PERSONID_EMPLOYMENT_RECORDS_GET, personId],
		queryFn: () => getPersonEmploymentRecords(personId ?? ''),
		enabled: !!personId,
	});

	const records = useMemo(
		() => (recordsQuery.data ?? []).filter((record) => record.employeeId === personId),
		[recordsQuery.data, personId]
	);

	const employersQuery = useQuery({
		queryKey: [API_PATHS.EMPLOYMENT_EMPLOYERS_GET],
		queryFn: getEmploymentEmployers,
		enabled: records.length > 0,
	});

	if (records.length === 0 || employersQuery.isLoading) {
		return null;
	}

	return (
		<InvestorDetailsCard
			title={tMipManage('investors.details.employment_card.title')}
			count={records.length}
			action={{ kind: 'expand' }}
			dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_EMPLOYMENT_CARD}
		>
			<Flex direction="column" gap="0.25rem">
				{records.map((record) => (
					<EmploymentRecordItem
						key={record.id}
						record={record}
						employerName={getEmployerNameById(
							employersQuery.data ?? [],
							record.employerId
						)}
					/>
				))}
			</Flex>
		</InvestorDetailsCard>
	);
};
